import { evaluateSelector } from '@coast/schemas';
import { isNil } from 'lodash';
import { CardFormType } from '@shared/scenes/entity/cardFormTypeContext';
import {
  useViewTemplate,
  useViewTemplates,
  useWorkflowTemplate,
} from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';

interface UseEntityFormViewTemplateProps {
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  selectorFields?: API.WorkflowEntity['fields'] | null;
  type: CardFormType;
}

export function useEntityFormViewTemplate(props: UseEntityFormViewTemplateProps) {
  return useEntityFormViewTemplateState(props).viewTemplate;
}

/**
 * The same resolution, with whether it has finished.
 *
 * For callers that treat a missing template as a statement about the workspace rather than as
 * something to render around: an absent template and a template still on its way look identical
 * otherwise, and saying the wrong one of those out loud is worse than saying nothing yet.
 */
export function useEntityFormViewTemplateState({
  workflowTemplateId,
  viewTemplateId,
  selectorFields,
  type,
}: UseEntityFormViewTemplateProps): {
  viewTemplate: API.ViewTemplateFragment | undefined;
  loading: boolean;
} {
  const { workflowTemplate, loading: workflowTemplateLoading } =
    useWorkflowTemplate(workflowTemplateId);

  const createSelectors = workflowTemplate?.behavior?.onNewCard?.selectors;
  const editSelectors = workflowTemplate?.behavior?.onOpenCard?.selectors;
  const selectors = type === 'CREATE' ? createSelectors : editSelectors;
  const behaviorSelector = selectors?.find(s => evaluateSelector(s.selector, selectorFields ?? {}));
  const behaviorTemplateId = behaviorSelector?.viewTemplateId;

  // Prefer the explicit route slug, then fall back to behavior-resolved ID.
  const resolvedViewTemplateId = viewTemplateId ?? behaviorTemplateId;

  const { viewTemplate: queriedViewTemplate, loading: viewTemplateLoading } = useViewTemplate(
    workflowTemplateId,
    resolvedViewTemplateId,
  );

  // onNewCard is currently always null, so behaviorTemplateId will not resolve
  // for the CREATE case — skip this query only when we have a known template ID.
  const { viewTemplates: cardViewTemplates, loading: cardViewTemplatesLoading } = useViewTemplates({
    workflowTemplateId,
    type: 'CARD',
    isPublic: false,
    skip: !isNil(resolvedViewTemplateId),
  });

  // Whichever of the two queries this resolution is using: the other is skipped, and a skipped query
  // never reports itself as loading.
  const loading = workflowTemplateLoading || viewTemplateLoading || cardViewTemplatesLoading;

  if (!isNil(resolvedViewTemplateId)) {
    return { viewTemplate: queriedViewTemplate, loading };
  }

  const onOpenCardSelectors = workflowTemplate?.behavior?.onOpenCard?.selectors;

  // For CREATE: if all but one template is referenced by onOpenCard selectors,
  // the unreferenced one is the create template.
  if (type === 'CREATE') {
    const createTemplate = findCreateTemplateByExclusion(cardViewTemplates, onOpenCardSelectors);
    if (!isNil(createTemplate)) {
      return { viewTemplate: createTemplate, loading };
    }
  }

  // For EDIT: if all onOpenCard selectors reference the same single template,
  // that is unambiguously the edit/open template regardless of selector conditions.
  if (type === 'EDIT') {
    const editTemplate = findEditTemplateBySingleReference(cardViewTemplates, onOpenCardSelectors);
    if (!isNil(editTemplate)) {
      return { viewTemplate: editTemplate, loading };
    }
  }

  return { viewTemplate: legacyFindViewTemplateByName(cardViewTemplates, type), loading };
}

function findCreateTemplateByExclusion(
  cardViewTemplates: API.ViewTemplateFragment[],
  onOpenCardSelectors?: API.NavigateAction['selectors'] | null,
): API.ViewTemplateFragment | undefined {
  if (isNil(onOpenCardSelectors) || cardViewTemplates.length <= 1) {
    return undefined;
  }
  const openCardTemplateIds = new Set(onOpenCardSelectors.map(s => s.viewTemplateId));
  const unreferenced = cardViewTemplates.filter(vt => !openCardTemplateIds.has(vt.id));
  return unreferenced.length === 1 ? unreferenced[0] : undefined;
}

function findEditTemplateBySingleReference(
  cardViewTemplates: API.ViewTemplateFragment[],
  onOpenCardSelectors?: API.NavigateAction['selectors'] | null,
): API.ViewTemplateFragment | undefined {
  if (isNil(onOpenCardSelectors) || onOpenCardSelectors.length === 0) {
    return undefined;
  }
  const uniqueReferencedIds = new Set(onOpenCardSelectors.map(s => s.viewTemplateId));
  if (uniqueReferencedIds.size !== 1) {
    return undefined;
  }
  const [templateId] = uniqueReferencedIds;
  return cardViewTemplates.find(vt => vt.id === templateId);
}

function legacyFindViewTemplateByName(
  cardViewTemplates: API.ViewTemplateFragment[],
  type: CardFormType,
): API.ViewTemplateFragment | undefined {
  // Further exploration here: https://app.shortcut.com/coast/story/46087/no-code-forms-refine-card-view-template-solution
  return (
    cardViewTemplates.find(vt =>
      type === 'CREATE'
        ? vt.name.toLowerCase().startsWith('create ')
        : !vt.name.toLowerCase().startsWith('create '),
    ) ?? cardViewTemplates[0]
  );
}
